import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "./supabaseClient";
import { S } from "./constants";

const JIRA_BASE = process.env.REACT_APP_JIRA_BASE_URL || "https://pdisoftware.atlassian.net";

// Jira statuses that belong in "Active" even though their category is "new"
const ACTIVE_STATUS_NAMES = new Set(["Needs Detail Design", "Needs Story Mapping"]);
// Jira statuses that belong in "Done" even if category isn't "done"
const DONE_STATUS_NAMES   = new Set(["Released"]);

export default function JiraView({ lanes, allProjects = [], onAddToBacklog }) {
  const [epics,      setEpics]      = useState({});
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [filterLane,  setFilterLane]  = useState("all");
  const [epicFilter,  setEpicFilter]  = useState("active"); // "active" | "backlog" | "done" | "all"
  const [collapsed,   setCollapsed]   = useState(new Set());

  const boardLanes = useMemo(() => lanes.filter((l) => l.jiraBoardId), [lanes]);
  const boardKey   = boardLanes.map((l) => `${l.id}:${l.jiraBoardId}`).join(",");

  const fetchEpics = useCallback(async () => {
    if (boardLanes.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("fetch-jira-epics", {
        body: { boards: boardLanes.map((l) => ({ laneId: l.id, boardId: l.jiraBoardId })) },
      });
      if (fnErr) throw fnErr;
      setEpics(data.epics || {});
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardKey]);

  useEffect(() => { fetchEpics(); }, [fetchEpics]);

  const visibleLanes = boardLanes.filter((l) => filterLane === "all" || l.id === filterLane);

  const isActive = (e) => e.statusCategory === "indeterminate" || ACTIVE_STATUS_NAMES.has(e.status);
  const isDone   = (e) => e.statusCategory === "done" || DONE_STATUS_NAMES.has(e.status);

  const filterEpics = (list) => {
    if (epicFilter === "active")  return list.filter((e) => isActive(e));
    if (epicFilter === "backlog") return list.filter((e) => !isActive(e) && !isDone(e));
    if (epicFilter === "done")    return list.filter((e) => isDone(e));
    return list; // "all"
  };

  const totalEpics = visibleLanes.reduce((sum, l) => sum + filterEpics(epics[l.id] || []).length, 0);

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"24px 32px" }}>
      <div style={{ display:"flex", gap:8, marginBottom:24, alignItems:"center", flexWrap:"wrap" }}>
        <span style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em", marginRight:4 }}>Filter</span>
        <select value={filterLane} onChange={(e) => setFilterLane(e.target.value)} style={S.filterSelect} aria-label="Filter by team">
          <option value="all">All Teams</option>
          {boardLanes.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
        </select>

        <div style={{ display:"flex", background:"var(--bg-subtle)", borderRadius:6, padding:2, gap:2, marginLeft:8 }}>
          {[{ id:"active", label:"Active" }, { id:"backlog", label:"Backlog" }, { id:"done", label:"Done" }, { id:"all", label:"All" }].map((m) => (
            <button key={m.id} onClick={() => setEpicFilter(m.id)} style={{
              padding:"4px 10px", borderRadius:4, border:"none", cursor:"pointer",
              fontSize:11, fontWeight:700, fontFamily:"inherit",
              background: epicFilter === m.id ? "var(--bg-card)" : "transparent",
              color:      epicFilter === m.id ? "var(--text-primary)" : "var(--text-secondary)",
              boxShadow:  epicFilter === m.id ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              transition:"all 0.15s",
            }}>{m.label}</button>
          ))}
        </div>

        <button onClick={fetchEpics} disabled={loading} style={{ ...S.btnGhost, fontSize:11, padding:"5px 10px", marginLeft:8 }}>
          {loading ? "⏳ Loading…" : "↺ Refresh"}
        </button>

        <span style={{ marginLeft:"auto", fontSize:11, color:"var(--text-muted)" }}>
          {totalEpics} epic{totalEpics !== 1 ? "s" : ""} · {boardLanes.length} team{boardLanes.length !== 1 ? "s" : ""}
        </span>
      </div>

      {boardLanes.length === 0 && (
        <div style={{ textAlign:"center", padding:"60px 0", color:"var(--text-muted)", fontSize:13 }}>
          No teams have a Jira board ID set.<br />
          <span style={{ fontSize:12 }}>Go to ☰ → Manage Teams to add a board ID to a team.</span>
        </div>
      )}

      {error && (
        <div style={{ padding:"12px 16px", borderRadius:8, background:"#FEF2F2", border:"1px solid #FECACA",
          color:"#DC2626", fontSize:12, fontWeight:600, marginBottom:16 }}>
          Failed to load epics: {error}
        </div>
      )}

      {loading && boardLanes.length > 0 && (
        <div style={{ textAlign:"center", padding:"60px 0", color:"var(--text-muted)", fontSize:13 }}>
          Loading epics from Jira…
        </div>
      )}

      {!loading && boardLanes.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:28 }}>
          {visibleLanes.map((lane) => {
            const all       = epics[lane.id] || [];
            const visible   = filterEpics(all);
            const hasError  = all.length === 1 && all[0].error;
            const isCollapsed = collapsed.has(lane.id);
            const toggle = () => setCollapsed(s => {
              const next = new Set(s);
              next.has(lane.id) ? next.delete(lane.id) : next.add(lane.id);
              return next;
            });

            return (
              <div key={lane.id}>
                <div onClick={toggle} style={{ display:"flex", alignItems:"center", gap:10, marginBottom: isCollapsed ? 0 : 10,
                  paddingBottom:8, borderBottom:"1px solid var(--border)", cursor:"pointer", userSelect:"none" }}
                  onMouseEnter={e => e.currentTarget.style.opacity="0.75"}
                  onMouseLeave={e => e.currentTarget.style.opacity="1"}>
                  <span style={{ fontSize:10, color:"var(--text-muted)", fontWeight:700, width:12, textAlign:"center", flexShrink:0 }}>
                    {isCollapsed ? "▶" : "▼"}
                  </span>
                  <div style={{ width:12, height:12, borderRadius:3, background:lane.color, flexShrink:0 }} />
                  <span style={{ fontSize:14, fontWeight:800, color:"var(--text-primary)", letterSpacing:"-0.02em" }}>
                    {lane.label}
                  </span>
                  <span style={{ fontSize:10, color:"var(--text-muted)", fontWeight:500 }}>
                    Board {lane.jiraBoardId}
                  </span>
                  <a href={`${JIRA_BASE}/jira/software/c/projects/GB/boards/${lane.jiraBoardId}`}
                    target="_blank" rel="noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{ fontSize:10, color:"var(--accent)", textDecoration:"none", fontWeight:600 }}>
                    Open board ↗
                  </a>
                  <span style={{ marginLeft:"auto", fontSize:10, fontWeight:700, background:"var(--bg-hover)",
                    color:"var(--text-secondary)", borderRadius:10, padding:"2px 8px", border:"1px solid var(--border)" }}>
                    {visible.length}
                  </span>
                </div>

                {!isCollapsed && hasError ? (
                  <div style={{ fontSize:12, color:"#DC2626", padding:"8px 12px", background:"#FEF2F2",
                    borderRadius:6, border:"1px solid #FECACA" }}>
                    Error fetching board {lane.jiraBoardId}: {all[0].error}
                  </div>
                ) : !isCollapsed && visible.length === 0 ? (
                  <div style={{ fontSize:12, color:"var(--text-muted)", padding:"8px 0" }}>
                    {epics[lane.id] ? "No epics match this filter." : "—"}
                  </div>
                ) : !isCollapsed ? (
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:10 }}>
                    {[...visible]
                      .map((epic) => ({ ...epic, _linked: allProjects.find((p) => p.jiraEpicKey === epic.key) }))
                      .sort((a, b) => (b._linked ? 1 : 0) - (a._linked ? 1 : 0))
                      .map((epic) => {
                      const linked = epic._linked;
                      return (
                      <div key={epic.id ?? epic.key} style={{
                        background: linked ? "var(--bg-subtle)" : "var(--bg-card)",
                        border: linked ? `1px solid ${lane.color}60` : "1px solid var(--border)",
                        borderRadius:8,
                        padding:"12px 14px", display:"flex", flexDirection:"column", gap:8,
                        boxShadow: linked ? `0 0 0 1px ${lane.color}30` : "0 1px 3px rgba(0,0,0,0.04)",
                        borderLeft:`3px solid ${lane.color}`,
                      }}>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                          <span style={{ fontSize:10, fontWeight:700, color:lane.color,
                            background:`${lane.color}18`, borderRadius:4, padding:"2px 7px",
                            border:`1px solid ${lane.color}40`, fontFamily:"monospace", letterSpacing:"0.02em" }}>
                            {epic.key}
                          </span>
                          <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                            {linked && (
                              <span style={{ fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:4,
                                background:"#E3FCEF", color:"#006644", border:"1px solid #79F2C0" }}
                                title={`Linked to: ${linked.name}`}>
                                ✓ Linked
                              </span>
                            )}
                            <span style={{
                              fontSize:9, fontWeight:700, padding:"2px 6px", borderRadius:4,
                              background: epic.statusCategory === "done" ? "#E3FCEF" : epic.statusCategory === "indeterminate" ? "#DEEBFF" : "#F4F5F7",
                              color:      epic.statusCategory === "done" ? "#006644" : epic.statusCategory === "indeterminate" ? "#0052CC" : "#42526E",
                              border:     `1px solid ${epic.statusCategory === "done" ? "#79F2C0" : epic.statusCategory === "indeterminate" ? "#4C9AFF" : "#C1C7D0"}`,
                            }}>
                              {epic.status || (epic.done ? "Done" : "Active")}
                            </span>
                          </div>
                        </div>

                        <div style={{ fontSize:12, fontWeight:600, color:"var(--text-primary)", lineHeight:1.45, flex:1 }}>
                          {epic.summary}
                        </div>

                        {linked && (
                          <div style={{ fontSize:10, color:"var(--text-muted)", fontStyle:"italic" }}>
                            → {linked.name}
                          </div>
                        )}

                        <div style={{ display:"flex", gap:8, alignItems:"center", justifyContent:"space-between" }}>
                          <a href={`${JIRA_BASE}/browse/${epic.key}`} target="_blank" rel="noreferrer"
                            style={{ fontSize:10, color:"var(--accent)", textDecoration:"none", fontWeight:600 }}>
                            Open in Jira ↗
                          </a>
                          {!linked && onAddToBacklog && (
                            <button
                              onClick={() => onAddToBacklog(epic, lane.id)}
                              style={{ fontSize:10, fontWeight:700, padding:"3px 9px", borderRadius:4,
                                background:"var(--accent)", color:"#fff", border:"none", cursor:"pointer",
                                fontFamily:"inherit", lineHeight:1.5 }}
                              onMouseEnter={e => e.currentTarget.style.opacity="0.85"}
                              onMouseLeave={e => e.currentTarget.style.opacity="1"}>
                              + Add to Roadmap
                            </button>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
